import { useEffect, useState } from "react";

// STYLES
import * as Style from "./DatePickerStyle";

// LIBRARIES
import moment from "moment";

// MISC
import { IProps } from "./DatePickerModel";
import { ButtonTypes } from "models/GeneralTypes/generalTypes";

// ASSETS
import PostsIconSelected from "assets/Icons/PostsIconUnselected.svg";

// REDUX

// COMPONENTS
import ButtonAtom from "components/Atoms/ButtonAtom/ButtonAtom";
import ToggleArrows from "components/ToggleArrows/ToggleArrows";

const DatePicker: React.FC<IProps> = (props: IProps) => {

    // PROPS
    const {
        startDate = null,
        endDate = null,
        isVisible = false,
        handleChangeStartDate = () => { },
        handleChangeEndDate = () => { },
        handleVisibilityChange = () => { },
        isSingleDatePicker = false,
    } = props;

    // CONSTANTS USING LIBRARIES

    // CONSTANTS USING HOOKS
    const [dayStart, setDayStart] = useState<number | null>(startDate ? startDate.getDate() : undefined);
    const [monthStart, setMonthStart] = useState<number>(startDate ? startDate.getMonth() : 0);
    const [yearStart, setYearStart] = useState<number>(startDate ? startDate.getFullYear() : 2023);
    const [dayEnd, setDayEnd] = useState<number | null>(endDate ? endDate.getDate() : undefined);
    const [monthEnd, setMonthEnd] = useState<number>(endDate ? endDate.getMonth() : 0);
    const [yearEnd, setYearEnd] = useState<number>(endDate ? endDate.getFullYear() : 2023);
    const [provStartDate, setProvStartDate] = useState<Date>(startDate ? startDate : undefined);
    const [provEndDate, setProvEndDate] = useState<Date>(endDate ? endDate : undefined);

    // GENERAL CONSTANTS
    const weekdays = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

    // USE EFFECT FUNCTION
    useEffect(() => {
        setProvStartDate(startDate);
        setProvEndDate(endDate);
        setDayStart(startDate ? startDate.getDate() : undefined)
        setMonthStart(startDate ? startDate.getMonth() : 0)
        setYearStart(startDate ? startDate.getFullYear() : 2023)
        setDayEnd(endDate ? endDate.getDate() : undefined)
        setMonthEnd(endDate ? endDate.getMonth() : 0)
        setYearEnd(endDate ? endDate.getFullYear() : 2023)
        //eslint-disable-next-line
    }, [startDate, endDate]);

    useEffect(() => {
        if (provStartDate && provEndDate && provStartDate > provEndDate) {
            setProvStartDate(provEndDate);
            setProvEndDate(provStartDate);
        }
    }, [provStartDate, provEndDate])

    // REQUEST API FUNCTIONS
    const isWithinRange = (date) => {
        if (provStartDate && provEndDate) {
            return date >= provStartDate && date <= provEndDate;
        }
        return false;
    };

    const getDaysInMonth = (month, year) => {
        return new Date(year, month + 1, 0).getDate();
    };

    const generateDays = (month, year, setDate, date, type) => {
        const daysInMonth = getDaysInMonth(month, year);
        return Array(daysInMonth).fill(0).map((_, index) => {
            const newDate = new Date(year, month, index + 1);
            const isActive = date
                && newDate.getDate() === date.getDate()
                && newDate.getMonth() === date.getMonth()
                && newDate.getFullYear() === date.getFullYear();
            return (
                <Style.Day
                    onClick={() => handleSelectDay(index + 1, setDate, type)}
                    isWithinRange={isWithinRange(newDate)}
                    isActive={isActive}
                    isDatePicker={isSingleDatePicker}
                    key={index}
                >
                    {index + 1}
                </Style.Day>
            );
        });
    };

    const handleSelectDay = (day, setDate, type) => {
        setDate(day);
        if (type === "start") {
            setProvStartDate(new Date(yearStart, monthStart, day))
        } else {
            setProvEndDate(new Date(yearEnd, monthEnd, day))
        }
    }

    const incrementMonth = (setMonth, setYear) => {
        setMonth(prevMonth => {
            if (prevMonth === 11) {
                setYear(prevYear => prevYear + 1);
                return 0;
            } else {
                return prevMonth + 1;
            }
        });
    };

    const decrementMonth = (setMonth, setYear) => {
        setMonth(prevMonth => {
            if (prevMonth === 0) {
                setYear(prevYear => prevYear - 1);
                return 11;
            } else {
                return prevMonth - 1;
            }
        });
    };

    const incrementYear = setYear => {
        setYear(prevYear => prevYear + 1);
    };

    const decrementYear = setYear => {
        let MIN_YEAR = 1998;
        setYear(prevYear => (prevYear > MIN_YEAR ? prevYear - 1 : prevYear));
    };

    const handleCancel = () => {
        handleVisibilityChange(false)
    };

    const handleConfirmDates = () => {
        handleChangeStartDate(provStartDate)
        handleChangeEndDate(provEndDate)
        handleCancel();
    };

    const handleRenderMonth = (month) => {
        switch (month) {
            case 0: return "JANUARY";
            case 1: return "FEBRUARY";
            case 2: return "MARCH";
            case 3: return "APRIL";
            case 4: return "MAY";
            case 5: return "JUNE";
            case 6: return "JULY";
            case 7: return "AUGUST";
            case 8: return "SEPTEMBER";
            case 9: return "OCTOBER";
            case 10: return "NOVEMBER";
            case 11: return "DECEMBER";
            default: return ""

        }
    };

    return (
        <>
            <Style.DateInputContainer onClick={() => handleVisibilityChange(true)}>
                <Style.Icon
                    src={PostsIconSelected}
                />
                {startDate && endDate ? <Style.Date>
                    {`${moment(startDate).format('DD/MM/YY')} - ${moment(endDate).format('DD/MM/YY')}`}
                </Style.Date>
                    : <Style.PlaceholderDate>
                        DD/MM/YY - DD/MM/YY
                    </Style.PlaceholderDate>
                }
            </Style.DateInputContainer>

            <Style.DatePickerDialog
                type="transparent"
                open={isVisible}
                onClose={() => handleVisibilityChange(false)}
            >
                <>
                    <Style.CalendarWrapper>
                        <Style.CalendarsContainer>
                            <Style.CalendarContainer>
                                <Style.CalendarHeader>
                                    <Style.Select>
                                        <ToggleArrows
                                            onUpArrowClick={() => incrementMonth(setMonthStart, setYearStart)}
                                            onDownArrowClick={() => decrementMonth(setMonthStart, setYearStart)}
                                            type="date_picker"
                                        />
                                        <Style.Month>
                                            {handleRenderMonth(monthStart)}
                                        </Style.Month>
                                    </Style.Select>
                                    <Style.Select>
                                        <ToggleArrows
                                            onUpArrowClick={() => incrementYear(setYearStart)}
                                            onDownArrowClick={() => decrementYear(setYearStart)}
                                            type="date_picker"
                                        />
                                        <Style.Year>
                                            {yearStart}
                                        </Style.Year>
                                    </Style.Select>
                                </Style.CalendarHeader>
                                <Style.WeekdaysRow>
                                    {weekdays.map(day => (
                                        <div key={day}>{day}</div>
                                    ))}
                                </Style.WeekdaysRow>
                                <Style.DaysGrid>
                                    {generateDays(monthStart, yearStart, setDayStart, provStartDate, "start")}
                                    <Style.Day />
                                </Style.DaysGrid>
                            </Style.CalendarContainer>
                            {!isSingleDatePicker && <Style.CalendarContainer>
                                <Style.CalendarHeader>
                                    <Style.Select>
                                        <ToggleArrows
                                            onUpArrowClick={() => incrementMonth(setMonthEnd, setYearEnd)}
                                            onDownArrowClick={() => decrementMonth(setMonthEnd, setYearEnd)}
                                            type="date_picker"
                                        />
                                        <Style.Month>
                                            {handleRenderMonth(monthEnd)}
                                        </Style.Month>
                                    </Style.Select>
                                    <Style.Select>
                                        <ToggleArrows
                                            onUpArrowClick={() => incrementYear(setYearEnd)}
                                            onDownArrowClick={() => decrementYear(setYearEnd)}
                                            type="date_picker"
                                        />
                                        <Style.Year>
                                            {yearEnd}
                                        </Style.Year>
                                    </Style.Select>
                                </Style.CalendarHeader>
                                <Style.WeekdaysRow>
                                    {weekdays.map(day => (
                                        <div key={day}>{day}</div>
                                    ))}
                                </Style.WeekdaysRow>
                                <Style.DaysGrid>
                                    {generateDays(monthEnd, yearEnd, setDayEnd, provEndDate, "end")}
                                    <Style.Day />
                                </Style.DaysGrid>
                            </Style.CalendarContainer>}
                        </Style.CalendarsContainer>
                        <Style.ControlsWrapper isDatePicker={isSingleDatePicker}>
                            <Style.ButtonContainer>
                                <ButtonAtom
                                    text="RENUNŢĂ"
                                    onClick={handleCancel}
                                    type={ButtonTypes.SECONDARY_BUTTON_CANCEL}
                                />
                            </Style.ButtonContainer>
                            <Style.ButtonContainer>
                                <ButtonAtom
                                    text="SALVEAZĂ"
                                    onClick={() => handleConfirmDates()}
                                    type={ButtonTypes.SECONDARY_BUTTON_DEFAULT}
                                />
                            </Style.ButtonContainer>
                        </Style.ControlsWrapper>
                    </Style.CalendarWrapper >
                </>
            </Style.DatePickerDialog>
        </>
    );
};

export default DatePicker;
